import { debounce } from "@solid-primitives/scheduled";
import { cookieStorage, makePersisted } from "@solid-primitives/storage";
import { useCallOptions, useChatClient, withBearerToken } from "@storytell/cp-client";
import { subscribeToGlobalEvent } from "@storytell/events";
import {
  type PromptEditorInstance,
  PromptStaticRenderer,
  parseStringIntoJSONContent,
  proseMirrorToRichText,
} from "@storytell/rich-text";
import type { Asset, AssetId, Concept, ConceptId, LabelId, ViewId } from "@storytell/types";
import { type PmDoc, PmNodeType, type VariableKind, assertExhaustive } from "@storytell/types/richText";
import { AnimateChildren } from "@storytell/ui/animation";
import { StButton } from "@storytell/ui/buttons";
import { StTooltip, StTooltipTrigger } from "@storytell/ui/tooltip";
import { TbArrowBackUp, TbArrowUpRight, TbHelpCircle, TbLoader2, TbPencil, TbX } from "solid-icons/tb";
import { Show, createEffect, createMemo, createSignal, on, onCleanup } from "solid-js";
import { createStore } from "solid-js/store";
import { Portal } from "solid-js/web";
import { twMerge } from "tailwind-merge";
import { useWire } from "~/wire";
import { usePromptBarContext } from "../PromptBarContext";
import { ImprovePromptsInfo } from "./ImprovePromptsToggle";
import { PromptBarButton } from "./PromptBarButton";
import { VariableV1StaticRenderer } from "./VariableV1Renderer/VariableV1Renderer";

/**
 * Provides state and functionality for auto-improving prompts.
 **/

export const useAutoImprove = (deps: {
  ctx: PromptEditorInstance;
}) => {
  const wire = useWire();
  const [state, setState] = makePersisted(createSignal("on" as "on" | "off" | "auto"), {
    storage: cookieStorage,
    storageOptions: {
      path: "/",
    },
    name: "aimprv:v1",
  });
  const [data, setData] = createStore({
    loading: false,
    improvement: null as PmDoc | null,
    improvementRaw: null as string | null,
    toUndo: false,
  });
  let oldContent = "";
  let isPromptBeingTyped = false;
  const attempts = new Map<Promise<unknown>, boolean>();

  const nextImproveState = () => {
    if (state() === "on") setState("auto");
    else if (state() === "auto") setState("off");
    else setState("on");
  };

  /**
   * Monitor updates to the editor content. If the user has paused typing for 2 seconds,
   * and there's more than 4 words,send the content to the server for auto-improvement.
   */
  const debouncedImproving = debounce(async () => {
    const content = deps.ctx.editor()?.getText() ?? "";

    // Check if there are at least 3 words in the content
    const words = content.trim().split(/\s+/).filter(Boolean);
    if (words.length < 3) {
      return;
    }

    setData({
      loading: true,
    });

    // Using sets to avoid duplicates
    const assets = new Set<string>();
    const concepts = new Set<string>();
    const labels = new Set<string>();
    const views = new Set<string>();

    try {
      const editor = deps.ctx.editor();
      if (editor) {
        editor.state.doc.descendants((node, pos) => {
          if (node.type && node.type.name === PmNodeType.VariableV1) {
            const variableType = node.attrs.type as VariableKind;
            switch (variableType) {
              case "asset":
                assets.add(node.attrs.id);
                break;
              case "concept":
                concepts.add(node.attrs.id);
                break;
              case "label":
                labels.add(node.attrs.id);
                break;
              case "view":
                views.add(node.attrs.id);
                break;
              default:
                // Exhaustive type checking - ensures all VariableKind values are handled
                assertExhaustive(variableType);
            }
          }
        });
      }
    } catch (error) {
      console.error("Error parsing prompt text mentions", error);
    }

    const chatClient = useChatClient();
    const promise = chatClient.improvePrompt(
      {
        prompt: content,
        scope: {
          assetIdList: Array.from(assets),
          conceptIdList: Array.from(concepts),
          labelIdList: Array.from(labels),
          viewIdList: Array.from(views),
        },
      },
      useCallOptions(withBearerToken(wire.auth.token())),
    );
    attempts.set(promise, true);

    const response = await promise;

    if (attempts.get(promise)) {
      // This is needed so that we can extract back any asset or concept mentions preserved by the improvement
      // prompt. We need to parse them by name since we don't pass the LLM the ids.
      const scope = wire.chat.scope;
      const assets = scope
        .assets()
        .map((a) => wire.assets.get(a as AssetId))
        .filter((a) => a !== undefined)
        .map((a) => ({ id: a.id, name: a.filename }));
      const concepts = scope
        .concepts()
        .map((c) => wire.concepts.get(c as ConceptId))
        .filter((c) => c !== undefined)
        .map((c) => ({ id: c.id, name: c.name }));
      const labels = scope
        .labels()
        .map((l) => wire.labels.get(l as LabelId))
        .filter((l) => l !== undefined)
        .map((l) => ({ id: l.id, name: l.name }));
      const views = scope
        .views()
        .map((v) => wire.views.get(v as ViewId))
        .filter((v) => v !== undefined)
        .map((v) => ({ id: v.id, name: v.name }));

      if (state() === "auto") {
        const richText = response.isOk()
          ? (parseStringIntoJSONContent(response.value.improvedPrompt, {
              extractAssets: assets,
              extractConcepts: concepts,
              extractLabels: labels,
              extractViews: views,
            }) as PmDoc)
          : null;
        const text = response.isOk() ? response.value.improvedPrompt : null;
        if (!richText || !text) return;
        setData({
          loading: false,
          improvementRaw: text,
          toUndo: true,
        });

        isPromptBeingTyped = true;
        await deps.ctx.typePrompt(richText);

        isPromptBeingTyped = false;
        return;
      }
      setData({
        loading: false,
        improvement: response.isOk()
          ? (parseStringIntoJSONContent(response.value.improvedPrompt, {
              extractAssets: assets,
              extractConcepts: concepts,
              extractLabels: labels,
              extractViews: views,
            }) as PmDoc)
          : null,
        improvementRaw: response.isOk() ? response.value.improvedPrompt : null,
      });
    }
  }, 1500);

  const onUpdate = () => {
    if (isPromptBeingTyped) return;

    const content = deps.ctx.editor()?.getText() ?? "";
    if (content === oldContent) {
      return;
    }
    oldContent = content;

    // A change has occurred, so we need to reset all previous auto-improvement attempts.
    attempts.clear();

    setData({
      improvement: null,
      improvementRaw: null,
      loading: false,
      toUndo: false,
    });

    if (content === "") {
      return;
    }

    // Check if there are at least 3 words in the content
    const words = content.trim().split(/\s+/).filter(Boolean);
    if (words.length < 3) {
      return;
    }

    debouncedImproving();
  };

  const editorMounted = createMemo(() => !!deps.ctx.editor());

  const clearImprovement = () => {
    attempts.clear();
    oldContent = "";
    isPromptBeingTyped = false;
    setData({
      improvement: null,
      improvementRaw: null,
      loading: false,
      toUndo: false,
    });
  };

  const hasUndo = () => !!data.toUndo;
  const undoSuggestion = async () => {
    const undo = data.toUndo;
    if (!undo) return;
    isPromptBeingTyped = true;
    deps.ctx.editor()?.commands.undo();
    isPromptBeingTyped = false;
    setData({
      toUndo: false,
    });
  };

  createEffect(
    on(
      () => editorMounted() && state() !== "off",
      (shouldRun) => {
        if (!shouldRun) return;
        const e = deps.ctx.editor();
        if (!e) return;

        onUpdate();
        e.on("update", onUpdate);

        const onSubmitted = () => {
          clearImprovement();
        };
        const unsubscribe = subscribeToGlobalEvent("prompt-submitted-successfuly", onSubmitted);
        onCleanup(() => {
          e.off("update", onUpdate);
          unsubscribe();
        });
      },
    ),
  );

  const onEditImprovement = async () => {
    const improvement = data.improvement;
    if (!improvement) return;
    isPromptBeingTyped = true;
    setData({
      improvement: null,
      improvementRaw: null,
      loading: false,
    });
    await deps.ctx.typePrompt(improvement);
    oldContent = deps.ctx.editor()?.getText() || "";
    isPromptBeingTyped = false;
  };

  return {
    nextImproveState,
    setImproveState: setState,
    improveState: state,
    improveSuggestion: () => data.improvement,
    improveSuggestionRaw: () => data.improvementRaw,
    improveLoading: () => data.loading,
    clearImprovement,
    onEditImprovement,
    hasUndo,
    undoSuggestion,
  };
};

export const PromptAutoImprove = () => {
  const ctx = usePromptBarContext();
  const {
    improveState,
    improveSuggestionRaw,
    improveLoading,
    clearImprovement,
    improveSuggestion,
    nextImproveState,
    onEditImprovement,
    hasUndo,
    undoSuggestion,
  } = ctx.autoImprove;

  return (
    <>
      <StTooltip openDelay={200} contentProps={{ class: "z-10" }} placement="top" content={<ImprovePromptsInfo />}>
        <StTooltipTrigger
          class="group/btn rounded-full cursor-pointer border border-muted-border dark:border-primary-border transition-colors pl-2 pr-1.5 h-8 text-[0.65rem] flex items-center hover:bg-panel-hover"
          onClick={nextImproveState}
        >
          <span class="mr-1.5">{improveLoading() ? "Improving..." : improveSuggestion() ? "Done!" : "Improve"}</span>
          <span
            class={twMerge(
              "group-hover/btn:scale-hover group-active/btn:scale-active transition-all h-5 w-7 ring uppercase tracking-wide text-[0.45rem] font-bold rounded-full grid place-content-center",
              improveState() === "on" &&
                "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-100 ring-emerald-200 dark:ring-emerald-800",
              improveState() === "off" && "bg-black/5 dark:bg-white/5 ring-black/10 dark:ring-white/10",
              improveState() === "auto" &&
                "bg-violet-100 text-purple-800 dark:bg-purple-900 dark:text-purple-100 ring-purple-200 dark:ring-purple-800",
            )}
          >
            <Show when={improveLoading()} fallback={improveState()}>
              <TbLoader2 class="animate-spin size-3" />
            </Show>
          </span>
        </StTooltipTrigger>
      </StTooltip>

      <Show when={hasUndo()}>
        <PromptBarButton onClick={undoSuggestion} class="text-xs px-2" icon={TbArrowBackUp}>
          Undo
        </PromptBarButton>
      </Show>
      <AnimateChildren
        enter={{
          opacity: [0, 1],
          y: [10, 0],
        }}
        exit={{
          opacity: [1, 0],
          y: [0, 19],
        }}
        show={improveState() === "on" && (!!improveSuggestion() || improveLoading())}
      >
        {(anim) => (
          <AutoImproveSuggestionPopup
            ref={anim.ref}
            onSubmit={() => {
              const suggestion = improveSuggestion();
              if (!improveLoading() && !!suggestion) {
                ctx.submission.handleSubmitWithContent(proseMirrorToRichText(suggestion), improveSuggestionRaw() || "");
                return;
              }
            }}
            onClose={clearImprovement}
            onEdit={onEditImprovement}
            loading={improveLoading()}
            improvedSuggestion={improveSuggestion()}
          />
        )}
      </AnimateChildren>
    </>
  );
};

const AutoImproveSuggestionPopup = (props: {
  ref: (el: HTMLElement) => void;
  onSubmit: () => void;
  onClose: () => void;
  onEdit: () => void;
  improvedSuggestion: PmDoc | null;
  loading: boolean;
}) => {
  return (
    <Portal mount={document.getElementById("prompt-bar") || undefined}>
      <div
        ref={props.ref}
        class="shadow-2xl absolute text-left bottom-[100%] mb-4 bg-panel flex flex-col items-start gap-2 rounded-xl w-full p-2 pl-4 ring-2 ring-violet-400 dark:ring-violet-700"
      >
        <div class="mb-2 flex gap-3 items-baseline justify-between w-full">
          <h4 class="font-medium text-xs text-muted-text">Recommended Prompt</h4>
          <div class="flex gap-3 items-center justify-betweend">
            <StButton disabled={props.loading} onClick={props.onEdit} icon={TbPencil} class="py-1">
              Edit
            </StButton>
            <StButton onClick={props.onClose} icon={TbX} class="py-1 px-1" />
          </div>
        </div>
        <Show
          when={props.improvedSuggestion}
          fallback={
            <span class="flex items-center text-sm gap-2">
              <TbLoader2 class="animate-spin" />
              <span>Improving your prompt.</span>
            </span>
          }
        >
          {(suggestion) => (
            <div class="text-sm max-h-24 overflow-y-auto">
              <PromptStaticRenderer doc={suggestion()} variableV1Renderer={VariableV1StaticRenderer} />
            </div>
          )}
        </Show>
        <div class="flex w-full items-center gap-2 flex-wrap justify-between">
          <a
            class="inline-flex items-center gap-1 underline text-brand text-xs"
            href="https://go.storytell.ai/learn-auto-improve"
            target="_blank"
            rel="noreferrer"
          >
            <TbHelpCircle />
            Learn how to make this automatic
          </a>

          <PromptBarButton
            onClick={props.onSubmit}
            class="rounded-lg [&_svg]:stroke-3 text-on-brand bg-brand px-3 text-sm hover:bg-brand-hover disabled:opacity-70"
            noBorder
            icon={TbArrowUpRight}
            disabled={props.loading}
            aria-label="Submit"
          >
            Submit this instead
          </PromptBarButton>
        </div>
      </div>
    </Portal>
  );
};
